import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plug, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiGet } from '../../lib/api.js';
import { Card, StubBanner, LoadingPanel } from './_shared.jsx';
import GoogleCalendarCard from './GoogleCalendarCard.jsx';
import IntegrityConnectCard from './IntegrityConnectCard.jsx';

const ROWS = [
  { key: 'meta', label: 'Meta (Facebook + Instagram)', deepLink: '', desc: 'Page access, lead-form ingestion, ad-account selection.' },
  { key: 'googleAds', label: 'Google Ads', deepLink: '/google-ads', desc: 'OAuth + conversion-action selection for offline conversions.' },
  { key: 'googleAdsConversions', label: 'Google Ads — Conversion Actions', deepLink: '/settings/integrations/google-ads', desc: 'Map CIVIX dispositions (sold, quoted, qualified call) to Google Ads offline-conversion actions + values.' },
  { key: 'twilio', label: 'Twilio', deepLink: '/phone-setup', desc: 'Voice + SMS provider. Numbers, A2P, recording.' },
  { key: 'sendgrid', label: 'Email (SendGrid / SMTP)', deepLink: '/settings#email', desc: 'Outbound transactional email — drips, invites, receipts.' },
  { key: 'stripe', label: 'Stripe', deepLink: '/billing', desc: 'Subscription billing + lead-credit top-ups.' },
  { key: 'openai', label: 'OpenAI / AI Brain', deepLink: '/devtools', desc: 'AI Assistant, dialer brain, drip composer.' },
];

export default function IntegrationsTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiGet('/settings/integrations').then((r) => {
      if (r?.ok) setData(r);
    }).catch((e) => toast.error(e.message || 'Failed to load integrations'));
  }, []);

  if (!data) return <LoadingPanel label="Loading integrations…" />;
  const { integrations, anyStub } = data;

  return (
    <div className="space-y-4">
      {anyStub && <StubBanner name="One or more integrations" />}

      <GoogleCalendarCard />

      <IntegrityConnectCard />


      <Card
        title="Integrations"
        description="External services CIVIX talks to. Connect / disconnect flows live on each integration's dedicated page."
      >
        <div className="space-y-2">
          {ROWS.map((row) => {
            const i = integrations[row.key] || {};
            return (
              <div key={row.key} className="flex items-center justify-between gap-3 p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Plug className="w-4 h-4 text-civix-purpleSoft mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {row.label}
                      {i.stub && (
                        <span className="pill bg-amber-500/15 text-amber-300 border-amber-500/30 inline-flex items-center gap-1 text-[10px]">
                          <AlertTriangle className="w-3 h-3" /> Stub Mode
                        </span>
                      )}
                      {i.connected && !i.stub && (
                        <span className="pill bg-emerald-500/15 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-1 text-[10px]">
                          <CheckCircle2 className="w-3 h-3" /> Connected
                        </span>
                      )}
                      {!i.connected && !i.stub && (
                        <span className="pill bg-white/5 text-white/50 border-white/10 text-[10px]">Not connected</span>
                      )}
                    </div>
                    <div className="text-xs text-white/45 mt-0.5">{row.desc}</div>
                    {row.key === 'twilio' && (
                      <div className="text-[11px] text-white/40 mt-1">
                        {i.numberCount} number{i.numberCount === 1 ? '' : 's'} · A2P brand: {i.a2pBrandStatus}
                      </div>
                    )}
                    {row.key === 'stripe' && i.plan && (
                      <div className="text-[11px] text-white/40 mt-1">Plan: {i.plan} · Customer: {i.customerId || '—'}</div>
                    )}
                    {row.key === 'sendgrid' && i.provider && (
                      <div className="text-[11px] text-white/40 mt-1">Provider: {i.provider}</div>
                    )}
                  </div>
                </div>
                {row.deepLink && (
                  <Link
                    to={row.deepLink}
                    className="btn-ghost text-xs inline-flex items-center gap-1.5 shrink-0"
                  >
                    Manage <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

